import { createWebHistory, createRouter } from 'vue-router'
import { HTTP } from '@/services/http'
import { UserService } from '@/services/user-service'
import { traverse } from '@/modules/util/object'

import appRoutes from './app'
import authRoutes from './auth'
import vehicleRoutes from './vehicles'
import inspectionRoutes from './inspections'

import NotFound from '@/views/NotFound.vue'
import { useInspectionStore } from '@/stores/inspection-store'
import { useVehicleStore } from '@/stores/vehicle-store'
import { Notify } from '@/modules/util/notify'

const routes = [
	...appRoutes,
	...authRoutes,
	...vehicleRoutes,
	...inspectionRoutes,
	{
		path: '/:pathMatch(.*)*', 
		name: 'not-found', 
		component: NotFound 
	},
]

const router = createRouter({
	history: createWebHistory(),
	routes,
})

HTTP.interceptors.request = (http, config) => {
	const key = config.method === 'GET' ? 'params' : 'body'
	const { username, usertoken } = UserService.getAuth()

	if (!config[key].username) {
		config[key].username = username
	}

	if (!config[key].usertoken) {
		config[key].usertoken = usertoken
	}

	const location_id = localStorage.getItem('LOCATION_ID')
	if (location_id) {
		config[key].location_id = parseInt(location_id)
	}


	// By only testing for username, we avoid blocking login requests.
	// We have the response hook as a fallback if the usertoken is missing.
	if (!config[key].username) {
		router.push({ name: 'login' })
		return false	// Cancel the request
	}

	return config
}

HTTP.interceptors.response = (http, res) => {
	if (res.body.errorType === 'AUTHENTICATION_FAILED') {
		router.push({ name: 'login' })
	}
    
	return res
}

HTTP.interceptors.cacheRequest = async (item) => {
	if (!item || !item.value || item.value.path !== '/saveinspection') {
		return item
	}

	let body, vehicle, inspection

	if (item.value.request && item.value.request.body) {
		try {
			body = JSON.parse(item.value.request.body)
			vehicle = body.vehicle ? JSON.parse(body.vehicle) : null
			inspection = body.inspection ? JSON.parse(body.inspection) : null
		} catch (e) {
			console.error('Failed to parse request body:', e)
			return null
		}
	}

	let ok = true, uids = []
    
	const inject = async (key, value, parent) => {
		if (!value || !value.uid) {
			return
		}

		const item = await HTTP.cache.get(value.uid)
		if (item === null) {
			return
		}
        
		if (!item.value?.response) {
			ok = false
			return
		}
		
		uids.push(value.uid)
		value.imagename = item.value.response.body.imagename

		if (value.image) {
			URL.revokeObjectURL(value.image)
			delete value.image
		}

		delete value.uid
	}

	if (vehicle) {
		await traverse(vehicle, inject)
	}

	if (inspection) {
		await traverse(inspection, inject)
	}

	body.vehicle = JSON.stringify(vehicle)
	body.inspection = JSON.stringify(inspection)
	item.value.request.body = JSON.stringify(body)

	await HTTP.cache.set(item.value, item.uid)

	uids.forEach(uid => {
		HTTP.cache.remove(uid)
	})

	return ok ? item : null
}

HTTP.interceptors.cacheResponse = (http, res, item) => {
	if (!res.status) {
		return
	}
    
	if (res.body && res.body.errorType === 'AUTHENTICATION_FAILED') {
		HTTP.cache.remove(item.uid)
		Notify.info('Authentication failed. Please log in')
		router.push({ name: 'login' })
		return
	}

	if (res.body?.imagename && item?.value?.path === '/uploadinspectionimage') {
		delete item.value.request
		item.value.response = res
		HTTP.cache.set(item.value, item.uid)
		return
	}

	HTTP.cache.remove(item.uid)

	if (!res.body?.inspection || item?.value?.path !== '/saveinspection') {
		return
	}

	Notify.success(`${
		res.body.inspection?.motorvehicleinspection_data?.vehicle?.registration || 
        res.body.inspection?.motorvehicleinspection_data?.vehicle_id
	}: Inspection uploaded`)

	const vehicleStore = useVehicleStore()
	const vehicle = vehicleStore.currentVehicle

	if (
		!vehicle?.id || 
        !res.body.inspection?.motorvehicle_id ||
        vehicle.id !== res.body.inspection.motorvehicle_id
	) {
		return
	}

	const inspectionStore = useInspectionStore()
	const inspections = inspectionStore.allInspections || []
	const inspection = res.body.inspection

	inspection.display_datetime = inspection.motorvehicleinspection_datetime
		.split(' ')
		.map((v, i) => {
			if (i) return v
			return v.split('-').reverse().join('/')
		})
		.join(' ')

	inspections.unshift(inspection)
	inspectionStore.setInspections(inspections)

	const data = inspection.motorvehicleinspection_data?.vehicle || {}

	Object.keys(data).forEach(key => {
		vehicle[key] = data[key]
	})

	vehicleStore.setCurrentVehicle(vehicle)
}

export default router